import type { PuppyAssemblyDocument, PuppyAssemblyNode } from './types'

/**
 * 装配持久化最终化结果。
 */
export interface FinalizePuppyAssemblyPersistenceResult {
  /** 已移除运行时 Object3D UUID 的可持久化装配文档。 */
  readonly assembly: PuppyAssemblyDocument
  /** 装配节点到导出事务内源对象 UUID 的内存绑定。 */
  readonly runtimeSourceObjectIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>>
}

/**
 * 读取未知值中的非空字符串。
 *
 * @param value 待读取值。
 * @returns 规整后的字符串；无效时返回空字符串。
 */
function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 建立运行时源对象 ID 到稳定装配节点 ID 的唯一映射。
 *
 * @param assembly 待最终化装配文档。
 * @param runtimeSourceObjectIdsByAssemblyNodeId 语义归一化阶段收集的运行时别名。
 * @returns runtime Object3D UUID 到 assemblyNodeId 的映射。
 */
function createAssemblyNodeIdByRuntimeSourceObjectId(
  assembly: PuppyAssemblyDocument,
  runtimeSourceObjectIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>> = {},
): ReadonlyMap<string, string> {
  const nodeIdBySourceObjectId = new Map<string, string>()
  const validNodeIds = new Set(assembly.nodes.map((node) => node.nodeId))
  const registerRuntimeSourceObjectId = (sourceObjectIdValue: unknown, nodeId: string): void => {
    const sourceObjectId = readText(sourceObjectIdValue)
    if (!sourceObjectId) {
      return
    }
    const existingNodeId = nodeIdBySourceObjectId.get(sourceObjectId)
    if (existingNodeId && existingNodeId !== nodeId) {
      throw new Error(
        `PuppyCAD 装配持久化存在重复运行时源对象：sourceObjectId=${sourceObjectId}, nodes=${existingNodeId},${nodeId}`,
      )
    }
    nodeIdBySourceObjectId.set(sourceObjectId, nodeId)
  }
  assembly.nodes.forEach((node) => {
    registerRuntimeSourceObjectId(node.sourceObjectId, node.nodeId)
  })
  Object.entries(runtimeSourceObjectIdsByAssemblyNodeId).forEach(([nodeId, sourceObjectIds]) => {
    if (!validNodeIds.has(nodeId)) {
      throw new Error(`PuppyCAD 装配持久化运行时绑定引用未知节点：assemblyNodeId=${nodeId}`)
    }
    sourceObjectIds.forEach((sourceObjectId) => {
      registerRuntimeSourceObjectId(sourceObjectId, nodeId)
    })
  })
  return nodeIdBySourceObjectId
}

/**
 * 收集项目 occurrence 映射仍需使用的导出事务内 UUID。
 *
 * @param node 当前装配节点。
 * @param additionalSourceObjectIds 语义归一化阶段折叠到当前节点的额外运行时 UUID。
 * @returns 去重并保持来源优先顺序的 runtime Object3D UUID。
 */
function collectRuntimeSourceObjectIds(
  node: PuppyAssemblyNode,
  additionalSourceObjectIds: readonly string[] = [],
): string[] {
  const sourceObjectId = readText(node.sourceObjectId)
  const selectionSourceObjectId = readText(node.extensions?.selectionSourceObjectId)
  return Array.from(new Set([
    sourceObjectId,
    selectionSourceObjectId,
    ...additionalSourceObjectIds.map(readText),
  ].filter(Boolean)))
}

/**
 * 创建不含运行时 Object3D UUID 的装配节点。
 *
 * @param node 原始装配节点。
 * @param nodeIdBySourceObjectId runtime UUID 到稳定节点 ID 的映射。
 * @param validNodeIds 文档内全部稳定装配节点 ID。
 * @returns 可写入 assembly.json 的节点克隆。
 */
function finalizePuppyAssemblyNode(
  node: PuppyAssemblyNode,
  nodeIdBySourceObjectId: ReadonlyMap<string, string>,
  validNodeIds: ReadonlySet<string>,
): PuppyAssemblyNode {
  const {
    sourceObjectId: _sourceObjectId,
    extensions: sourceExtensions,
    ...persistentNode
  } = node
  if (!sourceExtensions) {
    return persistentNode
  }

  const extensions = { ...sourceExtensions }
  const selectionSourceObjectId = readText(extensions.selectionSourceObjectId)
  const existingSelectionAssemblyNodeId = readText(extensions.selectionAssemblyNodeId)
  const mappedSelectionAssemblyNodeId = selectionSourceObjectId
    ? nodeIdBySourceObjectId.get(selectionSourceObjectId)
    : undefined
  if (selectionSourceObjectId && !mappedSelectionAssemblyNodeId) {
    throw new Error(
      `PuppyCAD 装配节点无法映射选择代理：nodeId=${node.nodeId}, selectionSourceObjectId=${selectionSourceObjectId}`,
    )
  }
  const resolvedSelectionAssemblyNodeId = mappedSelectionAssemblyNodeId
    ?? existingSelectionAssemblyNodeId
  delete extensions.selectionSourceObjectId
  delete extensions.selectionAssemblyNodeId
  if (resolvedSelectionAssemblyNodeId && resolvedSelectionAssemblyNodeId !== node.nodeId) {
    if (!validNodeIds.has(resolvedSelectionAssemblyNodeId)) {
      throw new Error(
        `PuppyCAD 装配节点引用未知选择节点：nodeId=${node.nodeId}, selectionAssemblyNodeId=${resolvedSelectionAssemblyNodeId}`,
      )
    }
    extensions.selectionAssemblyNodeId = resolvedSelectionAssemblyNodeId
  }

  return {
    ...persistentNode,
    ...(Object.keys(extensions).length > 0 ? { extensions } : {}),
  }
}

/**
 * 将装配文档中的运行时 Object3D 引用最终化为稳定持久化引用。
 *
 * `sourceObjectId` 与 `selectionSourceObjectId` 只服务当前导出事务，刷新页面后必然失效。
 * 本函数把可恢复的选择代理转换为 assemblyNodeId，并将原 UUID 保存到不参与序列化的内存绑定表，
 * 供同一事务内生成 `project-model.json` 时关联项目节点。
 *
 * @param assembly 已完成语义重映射和 Mate 归一化的装配文档。
 * @param runtimeSourceObjectIdsByAssemblyNodeId 被折叠运行时载体到最终装配节点的事务绑定。
 * @returns 稳定装配文档与 runtime binding。
 */
export function finalizePuppyAssemblyPersistence(
  assembly: PuppyAssemblyDocument,
  runtimeSourceObjectIdsByAssemblyNodeId: Readonly<Record<string, readonly string[]>> = {},
): FinalizePuppyAssemblyPersistenceResult {
  const nodeIdBySourceObjectId = createAssemblyNodeIdByRuntimeSourceObjectId(
    assembly,
    runtimeSourceObjectIdsByAssemblyNodeId,
  )
  const validNodeIds = new Set(assembly.nodes.map((node) => node.nodeId))
  const finalizedRuntimeSourceObjectIdsByAssemblyNodeId = Object.fromEntries(
    assembly.nodes.map((node) => [
      node.nodeId,
      collectRuntimeSourceObjectIds(node, runtimeSourceObjectIdsByAssemblyNodeId[node.nodeId]),
    ]),
  )
  return {
    assembly: {
      ...assembly,
      nodes: assembly.nodes.map((node) => finalizePuppyAssemblyNode(
        node,
        nodeIdBySourceObjectId,
        validNodeIds,
      )),
    },
    runtimeSourceObjectIdsByAssemblyNodeId: finalizedRuntimeSourceObjectIdsByAssemblyNodeId,
  }
}
